import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, Check, Crown, Sparkles, Zap, Lock, Users } from "lucide-react";
import api from "../lib/apiClient";
import { toast } from "sonner";
import { trackEvent, trackAuthed } from "../lib/analytics";

const ENT_LABELS = {
  BASIC_TASKS: "Tâches", BASIC_PROJECTS: "Projets", BASIC_XP: "XP & niveaux",
  TEXT_PROOF: "Preuve texte", IMAGE_PROOF: "Preuve image", AUDIO_PROOF: "Preuve audio",
  VIDEO_PROOF: "Preuve vidéo", LINK_PROOF: "Preuve lien", AI_PROOF_REVIEW: "Validation IA des preuves",
  UNLIMITED_PROJECTS: "Projets illimités", ADVANCED_COACH: "Coach avancé", RELEASE_PLANNER: "Smart Release Planner",
  CONTENT_STUDIO: "Content Studio", ADVANCED_ANALYTICS: "Analytics avancées", GROWTH: "Growth / Audience",
  MARKETING_AUTOPILOT: "Marketing Autopilot", EXTERNAL_CONNECTORS: "Connecteurs externes",
  ADVANCED_AI: "IA avancée", AUTOMATIONS: "Automatisations",
  MANAGER_DASHBOARD: "Tableau de bord manager", MANAGER_ARTISTS: "Gestion des membres",
  MANAGER_INVITATIONS: "Invitations", MANAGER_VALIDATION: "Validation des livrables",
  MANAGER_STATISTICS: "Statistiques", MANAGER_CALENDAR: "Calendrier partagé",
};

const PLAN_META = {
  FREE: { name: "Free", icon: Zap, tagline: "La boucle Cehnura, gratuitement.", color: "text-zinc-300" },
  PREMIUM: { name: "Premium", icon: Sparkles, tagline: "Preuves média, coach avancé, sorties.", color: "text-amber-400" },
  PRO: { name: "Pro", icon: Crown, tagline: "Growth, Autopilot & automatisations.", color: "text-amber-300" },
};

const PLAN_ENT = {
  FREE: ["BASIC_TASKS", "BASIC_PROJECTS", "BASIC_XP", "TEXT_PROOF", "IMAGE_PROOF"],
  PREMIUM: ["AUDIO_PROOF", "VIDEO_PROOF", "LINK_PROOF", "AI_PROOF_REVIEW", "UNLIMITED_PROJECTS", "ADVANCED_COACH", "RELEASE_PLANNER", "CONTENT_STUDIO", "ADVANCED_ANALYTICS"],
  PRO: ["GROWTH", "MARKETING_AUTOPILOT", "EXTERNAL_CONNECTORS", "ADVANCED_AI", "AUTOMATIONS"],
};
const ORDER = ["FREE", "PREMIUM", "PRO"];

// Taglines adaptées au type d'utilisateur (les fonctionnalités restent les mêmes, le discours change).
const PROFILE_TAGLINES = {
  PROFESSIONAL: {
    FREE: "Ton activité pro, gratuitement.",
    PREMIUM: "Portfolio média, devis illimités, mise en avant.",
    PRO: "Croissance, automatisations & pilotage avancé.",
  },
};

export default function Subscription() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");

  const load = async () => setData((await api.get("/subscription")).data);
  useEffect(() => { load(); trackEvent("pricing_viewed", { source: "subscription" }); }, []);

  const upgrade = async (plan) => {
    setBusy(plan);
    try {
      trackAuthed("checkout_started", { plan, source: "subscription" });
      const { data } = await api.post("/subscription/checkout", { plan, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch {
      toast.error("Impossible de lancer le paiement.");
      setBusy("");
    }
  };

  if (!data) return <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 text-amber-500 animate-spin" /></div>;
  const current = data.plan;

  const manage = async () => {
    try {
      const { data: d } = await api.post("/subscription/portal", { origin_url: window.location.origin });
      window.location.href = d.url;
    } catch {
      toast.error("Portail de facturation indisponible.");
    }
  };

  const space = data.profile?.active_space || "CREATOR";

  if (space === "MANAGER") {
    const mgrEnts = data.manager_entitlements || [];
    return (
      <div className="space-y-6 max-w-3xl" data-testid="subscription-page">
        <div>
          <h1 className="font-display text-3xl font-extrabold flex items-center gap-2"><Crown className="w-7 h-7 text-amber-400" /> Abonnement</h1>
          <p className="text-zinc-500 text-sm mt-1">Espace Manager — <span className="text-emerald-400 font-semibold">gratuit</span>.</p>
        </div>
        <div className="rounded-3xl p-6 border border-emerald-500/40 bg-emerald-500/5" data-testid="plan-card-MANAGER">
          <div className="flex items-center gap-2 mb-1"><Users className="w-5 h-5 text-emerald-400" /><span className="font-display text-xl font-bold">Manager</span>
            <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/40">Actuel</span></div>
          <p className="text-xs text-zinc-500 mb-3">Gère tes membres, invitations, validations et le commercial — sans frais.</p>
          <p className="font-display text-2xl font-extrabold mb-4">0€<span className="text-sm text-zinc-500 font-normal">/mois</span></p>
          <ul className="space-y-2">
            {mgrEnts.map((e) => (
              <li key={e} className="flex items-center gap-2 text-sm text-zinc-300"><Check className="w-4 h-4 text-emerald-400 shrink-0" /> {ENT_LABELS[e] || e}</li>
            ))}
          </ul>
        </div>
        <p className="text-xs text-zinc-500" data-testid="manager-free-note">L'espace Manager est 100% gratuit. Les offres Premium/Pro concernent les espaces Créateur et Professionnel.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-5xl" data-testid="subscription-page">
      <div>
        <h1 className="font-display text-3xl font-extrabold flex items-center gap-2"><Crown className="w-7 h-7 text-amber-400" /> Abonnement</h1>
        <p className="text-zinc-500 text-sm mt-1">Ton plan actuel : <span className="text-amber-400 font-semibold" data-testid="current-plan">{PLAN_META[current]?.name}</span>. Débloque plus de puissance quand tu es prêt.</p>
        {current !== "FREE" && (
          <button onClick={manage} data-testid="manage-subscription-btn" className="mt-3 h-10 px-4 rounded-xl border border-amber-500/40 text-amber-300 text-sm font-medium hover:bg-amber-500/10 transition-colors">
            Gérer mon abonnement
          </button>
        )}
      </div>

      {data.free_mode && (
      <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 px-5 py-4 flex items-start gap-3" data-testid="free-mode-banner">
        <Sparkles className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-emerald-300">Tout est gratuit en ce moment 🎉</p>
          <p className="text-xs text-emerald-200/70 mt-0.5">Toutes les fonctionnalités (Coach avancé, Sorties, Content Studio, Analytics, Growth, Autopilot…) sont débloquées pour tout le monde. Aucun paiement requis pour l'instant.</p>
        </div>
      </div>
      )}

      <div className="grid gap-4 md:grid-cols-3">
        {ORDER.map((plan) => {
          const meta = PLAN_META[plan];
          const Icon = meta.icon;
          const isCurrent = current === plan;
          const price = data.pricing?.[plan];
          const cumulative = plan === "FREE" ? PLAN_ENT.FREE : plan === "PREMIUM" ? [...PLAN_ENT.FREE, ...PLAN_ENT.PREMIUM] : [...PLAN_ENT.FREE, ...PLAN_ENT.PREMIUM, ...PLAN_ENT.PRO];
          const canUpgrade = ORDER.indexOf(plan) > ORDER.indexOf(current);
          return (
            <motion.div key={plan} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              className={`rounded-3xl p-6 border flex flex-col ${isCurrent ? "border-amber-500/60 bg-amber-500/5 glow-gold" : "border-zinc-800 bg-[#18181E]"}`}
              data-testid={`plan-card-${plan}`}>
              <div className="flex items-center gap-2 mb-1"><Icon className={`w-5 h-5 ${meta.color}`} /><span className="font-display text-xl font-bold">{meta.name}</span>
                {isCurrent && <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 border border-amber-500/40">Actuel</span>}</div>
              <p className="text-xs text-zinc-500 mb-3">{(PROFILE_TAGLINES[space] && PROFILE_TAGLINES[space][plan]) || meta.tagline}</p>
              <div className="mb-4">
                {price ? <p className="font-display text-2xl font-extrabold">{price.amount.toFixed(2).replace(".", ",")}€<span className="text-sm text-zinc-500 font-normal">/mois</span></p>
                       : <p className="font-display text-2xl font-extrabold">0€</p>}
              </div>
              <ul className="space-y-2 flex-1 mb-4">
                {cumulative.map((e) => (
                  <li key={e} className="flex items-center gap-2 text-sm text-zinc-300"><Check className="w-4 h-4 text-emerald-400 shrink-0" /> {ENT_LABELS[e]}</li>
                ))}
              </ul>
              {isCurrent ? (
                <button disabled className="w-full h-11 rounded-xl border border-zinc-700 text-zinc-400 text-sm font-medium" data-testid={`plan-current-${plan}`}>Plan actuel</button>
              ) : canUpgrade ? (
                <button onClick={() => upgrade(plan)} disabled={busy === plan} data-testid={`upgrade-${plan}`}
                  className="w-full h-11 rounded-xl gold-gradient text-black font-semibold flex items-center justify-center gap-2 hover:opacity-90 disabled:opacity-60">
                  {busy === plan ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Passer à {meta.name}</>}
                </button>
              ) : (
                <button disabled className="w-full h-11 rounded-xl border border-zinc-800 text-zinc-600 text-sm flex items-center justify-center gap-2"><Lock className="w-4 h-4" /> Inclus</button>
              )}
            </motion.div>
          );
        })}
      </div>
      {current === "FREE" && data.limits && (
        <p className="text-xs text-zinc-500" data-testid="free-limits">Limites Free : {data.limits.active_projects} projet actif · {data.limits.max_projects} projets max · preuves texte & image uniquement.</p>
      )}
    </div>
  );
}
